"use client";

/**
 * ConsentPreferenceCenter — modal for reviewing and changing the consent
 * decision at any time. Reachable from the footer on every page in both
 * locales, and from the "Preferences" button on the first-visit banner.
 *
 * Opens via the exported `openConsentPreferences()` helper (subscribable
 * via `sc-consent-open` window event). This keeps the modal state local
 * without needing a global context provider.
 *
 * Accessibility:
 *   - role="dialog", aria-modal="true", labelled by the title.
 *   - Focus is moved to the close button on open and returned to the
 *     opener on close.
 *   - Escape closes.
 *   - Body scroll is locked while open.
 *   - Toggle is a real `<button role="switch" aria-checked>` — screen
 *     readers announce state and the space/enter keys flip it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import {
  readConsent,
  setAnalyticsConsent,
  clearNonEssentialStorage,
} from "@/lib/consent";

const OPEN_EVENT = "sc-consent-open";

/** Fire the open event. Safe to call from server components (no-op on SSR). */
export function openConsentPreferences(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  } catch {
    /* ignore */
  }
}

export function ConsentPreferenceCenter() {
  const { t, language } = useTranslation();
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState<"granted" | "denied">("denied");
  const [savedMessage, setSavedMessage] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const refreshFromStorage = useCallback(() => {
    const rec = readConsent();
    setAnalytics(rec?.analytics === "granted" ? "granted" : "denied");
  }, []);

  useEffect(() => {
    const onOpen = () => {
      openerRef.current = (document.activeElement as HTMLElement) ?? null;
      refreshFromStorage();
      setSavedMessage(false);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [refreshFromStorage]);

  // Body-scroll lock + focus management + escape.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Move focus into the dialog.
    const focusTimer = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Return focus to the opener.
      openerRef.current?.focus?.();
    }
  }, [open]);

  const commit = useCallback((value: "granted" | "denied") => {
    // If we are withdrawing, sweep known non-essential keys first so the
    // withdrawal takes effect without a reload trick.
    if (value === "denied") clearNonEssentialStorage();
    setAnalyticsConsent(value);
    setAnalytics(value);
    setSavedMessage(true);
  }, []);

  if (!open) return null;

  const privacyHref = language === "bg" ? "/bg/legal/privacy" : "/legal/privacy";
  const analyticsOn = analytics === "granted";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sc-consent-prefs-title"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
      onClick={(e) => {
        // Click on backdrop closes; clicks inside the panel bubble stop below.
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-border bg-surface shadow-xl overflow-hidden"
        role="document"
      >
        <div className="flex items-start justify-between p-5 border-b border-border">
          <h2
            id="sc-consent-prefs-title"
            className="text-base font-semibold text-text-primary"
          >
            {t("consent_prefs_title")}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t("consent_prefs_close")}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-text-primary hover:bg-hover-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span aria-hidden>×</span>
          </button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("consent_prefs_intro")}{" "}
            <Link
              href={privacyHref}
              className="underline hover:text-text-primary"
            >
              {t("consent_banner_learn_more")}
            </Link>
            .
          </p>

          {/* Essential */}
          <section className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between gap-3 mb-1">
              <h3 className="text-sm font-semibold text-text-primary">
                {t("consent_prefs_essential_title")}
              </h3>
              <span className="text-xs text-muted-foreground">
                {t("consent_prefs_essential_state")}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("consent_prefs_essential_body")}
            </p>
          </section>

          {/* Analytics */}
          <section className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between gap-3 mb-1">
              <h3 className="text-sm font-semibold text-text-primary">
                {t("consent_prefs_analytics_title")}
              </h3>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsOn}
                aria-label={t("consent_prefs_toggle_aria")}
                onClick={() => setAnalytics(analyticsOn ? "denied" : "granted")}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  analyticsOn ? "bg-primary" : "bg-hover-bg"
                }`}
              >
                <span
                  aria-hidden
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    analyticsOn ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("consent_prefs_analytics_body")}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {analyticsOn
                ? t("consent_prefs_analytics_state_granted")
                : t("consent_prefs_analytics_state_denied")}
            </p>
          </section>

          {savedMessage && (
            <p
              className="text-xs text-primary"
              role="status"
              aria-live="polite"
            >
              {t("consent_prefs_status_saved")}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end p-5 border-t border-border">
          <button
            type="button"
            onClick={() => commit("denied")}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-primary hover:bg-hover-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t("consent_prefs_reject_all")}
          </button>
          <button
            type="button"
            onClick={() => commit("granted")}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-primary hover:bg-hover-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t("consent_prefs_accept_all")}
          </button>
          <button
            type="button"
            onClick={() => commit(analytics)}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-text-primary text-sm font-semibold hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t("consent_prefs_save")}
          </button>
        </div>
      </div>
    </div>
  );
}
